import { memo, useCallback, useEffect, useState } from "react";
import { makeStyles } from "tss-react/mui";
import {
  MiBuildPageHeader,
  MiButton,
  MiButtonGroup,
  MiDateRangePicker,
  MiIcon,
  MiIconButton,
  MiSearchInput,
  MiTab,
} from "@miplatform/components";
import {
  BUTTON_COLORS,
  BUTTON_VARIANTS,
  EMAIL_ENTITY_TYPE,
} from "@miplatform/constants";
import { useRouter } from "@miplatform/hooks";
import {
  mdiCalendar,
  mdiCalendarRefresh,
  mdiFileTableOutline,
  mdiFormatListBulleted,
  mdiMap,
  mdiPlus,
  mdiPrinterOutline,
} from "@mdi/js";
import {
  EmailActivityPreviewModal,
  OrderActivityDrawer,
} from "@mibuildweb/components";
import { Box, Divider } from "@mui/material";
import { useOrderTemplates } from "../../hooks/useOrderTemplate/useOrderTemplates";
import { AssignmentsCalendar } from "./AssignmentsCalendar/AssignmentsCalendar";
import { AssignmentsPageView } from "./AssignmentsCalendar/AssignmentsCalendar.constants";
import { OrderTemplatesFilter } from "./OrderTemplatesFilter/OrderTemplatesFilter";
import { OrdersEmailDrawer } from "./OrdersEmailDrawer/OrdersEmailDrawer";
import { CapacityTrackerList } from "./OrdersList/CapacityTrackerList/CapacityTrackerList";
import { OrdersList } from "./OrdersList/OrdersList";
import { OrdersPrintModal } from "./OrdersPrintModal/OrdersPrintModal";
import { OrdersProvider, useOrdersContext } from "./OrdersProvider";
import { OrdersSearch } from "./OrdersSearch/OrdersSearch";
import {
  ORDERS_DATE_PICKER_CALENDER_COUNT,
  ORDERS_DATE_PICKER_MAX_SELECTION_DAYS,
  ORDERS_TABS_NAMES,
} from "./ordersConstants";
import { useOrdersViewModel } from "./useOrdersViewModel";

/**
 * The `Orders` component is a page that displays and manages order data.
 * It includes features such as date range filtering, tabbed navigation, and a list of orderItems.
 *
 * @component
 * @returns {JSX.Element} The rendered `Orders` component.
 */
export function OrdersContent() {
  const {
    isPrintModalOpen,
    orderCardConfig,
    orderHoverCardConfig,
    activeTab,
    filters,
    orderTypeCount,
    tabs,
    refresh,
    orderItems,
    debouncedSearch,
    isLoading,
    search,
    orderReasons,
    isCapacityTrackerVisible,
    setOrderItems,
    getOrderItems,
    setSearch,
    isDateRangeDisabled,
    getOrderItemsCount,
    getOrderTypesCount,
    getAccount,
    tenantHolidays,
    getTenantHolidays,
    getHoldReasons,
    getRescheduleReasons,
    getOrderCardConfig,
    handlePrintModalOpen,
    handlePrintModalClose,
    handleSpreadsheetDownload,
    handleFilterChange,
    handleResetDateRange,
    handleRefresh,
    handleTabChange,
    handlePrintOrders,
    sidebarFilters,
    initialSidebarFilters,
    setSidebarFilters,
  } = useOrdersViewModel();
  const { classes, theme } = useStyles({ isCapacityTrackerVisible });
  const { orderTemplates, getOrderTemplates } = useOrderTemplates();
  const [assignmentsPageView, setAssignmentsPageView] = useState(
    AssignmentsPageView.Calendar
  );

  const { navigate } = useRouter();

  const {
    emailDrawer,
    emailPreview,
    isOrderActivityDrawerOpen,
    closeDrawer,
    handleEmailDrawerClose,
    handleEmailPreviewModalClose,
    selectedOrder,
    setSelectedOrder,
    getOrderActivity,
  } = useOrdersContext();

  const handleSearch = useCallback(
    (e) => setSearch(e.target.value),
    [setSearch]
  );

  useEffect(() => {
    getAccount();
    getTenantHolidays();
    getHoldReasons();
    getRescheduleReasons();
    getOrderCardConfig();
  }, []);

  useEffect(() => {
    handleRefresh();
  }, [activeTab, filters]);

  useEffect(() => {
    setOrderItems([]);
    closeDrawer();
  }, [activeTab]);

  useEffect(() => {
    getOrderTypesCount();
  }, [activeTab, refresh, debouncedSearch]);

  useEffect(() => {
    getOrderItemsCount();
  }, [refresh]);

  useEffect(() => {
    getOrderTemplates({ filters: { requiresApproval: true } });
  }, []);

  return (
    <>
      <MiBuildPageHeader
        title="Orders"
        titleIcon={
          <MiIcon
            path={mdiFormatListBulleted}
            size={1}
            color={theme.palette.primary.main}
          />
        }
        loading={isLoading}
        style={{ gap: theme.spacing(2) }}
        buttonGroupComponent={
          <Box display="flex" gap={1} alignItems="center">
            {activeTab.value === ORDERS_TABS_NAMES.ASSIGNMENTS && (
              <MiButtonGroup
                condensed
                selected={assignmentsPageView}
                data={[
                  {
                    label: "Map",
                    value: AssignmentsPageView.Map,
                    icon: mdiMap,
                  },
                  {
                    label: "Calendar",
                    value: AssignmentsPageView.Calendar,
                    icon: mdiCalendar,
                  },
                ]}
                containerHeight={35}
                handleClick={(item) => setAssignmentsPageView(item.value)}
              />
            )}
            <MiSearchInput
              value={search}
              handleChange={handleSearch}
              className={classes.searchField}
            />
          </Box>
        }
        actionComponent={
          <Box className={classes.actionContainer}>
            <MiButton
              title="Create New"
              variant={BUTTON_VARIANTS.CONTAINED}
              startIcon={{
                path: mdiPlus,
              }}
              onClick={() => navigate("/scheduling/orders/create")}
              disabled
            />
            <MiIconButton
              data-testid="mi-orders-spreadsheet-icon-button"
              disabled={!orderItems.length}
              className={classes.exportButton}
              onClick={handleSpreadsheetDownload}
            >
              <MiIcon
                path={mdiFileTableOutline}
                color={theme.palette.common.grey}
              />
            </MiIconButton>
            <MiIconButton
              data-testid="mi-orders-print-icon-button"
              disabled={!orderItems.length || activeTab.disablePrint}
              className={classes.exportButton}
              onClick={handlePrintModalOpen}
            >
              <MiIcon
                path={mdiPrinterOutline}
                color={theme.palette.common.grey}
              />
            </MiIconButton>
          </Box>
        }
      />
      <MiTab
        tabs={tabs}
        currentTab={activeTab.value}
        onTabChange={handleTabChange}
      />
      {activeTab.value === ORDERS_TABS_NAMES.ALL && (
        <OrdersSearch
          search={debouncedSearch}
          setSearch={setSearch}
          onEnterPress={handleRefresh}
          setOrders={setOrderItems}
          orders={orderItems}
          getOrders={getOrderItems}
          isFetchingOrders={isLoading}
          orderCardConfig={orderCardConfig}
          orderHoverCardConfig={orderHoverCardConfig}
          refresh={refresh}
          onRefresh={handleRefresh}
          refreshPagination={activeTab}
          orderReasons={orderReasons}
          filters={sidebarFilters}
          initialFilters={initialSidebarFilters}
          setFilters={setSidebarFilters}
        />
      )}
      {activeTab.value === ORDERS_TABS_NAMES.ASSIGNMENTS && (
        <AssignmentsCalendar view={assignmentsPageView} />
      )}
      {activeTab.value !== ORDERS_TABS_NAMES.ALL &&
        activeTab.value !== ORDERS_TABS_NAMES.ASSIGNMENTS && (
          <Box className={classes.listContainer} margin={theme.spacing(3)}>
            <OrderTemplatesFilter
              orderTemplates={orderTemplates}
              orderTypeCount={orderTypeCount}
              filters={filters}
              onFilterChange={handleFilterChange}
            />
            <Divider
              orientation="vertical"
              flexItem
              sx={{ paddingLeft: theme.spacing(2) }}
            />
            <Box
              display="flex"
              flex={1}
              flexDirection="column"
              height="100%"
              overflow="hidden"
            >
              {isCapacityTrackerVisible && (
                <CapacityTrackerList
                  refresh={refresh}
                  selectedOrderTemplate={filters.selectedOrderTemplate}
                />
              )}
              <Box
                display="flex"
                flexDirection="column"
                height="100%"
                overflow="auto"
              >
                {activeTab.dateRange && (
                  <Box
                    display="flex"
                    alignItems="center"
                    justifyContent="center"
                    my={2}
                    gap={2}
                  >
                    <MiDateRangePicker
                      showShortCutItems
                      showThisWeekButton={false}
                      dateRange={filters.dateRange}
                      onDateChange={(startDate, endDate) =>
                        handleFilterChange({
                          dateRange: { startDate, endDate },
                        })
                      }
                      calendarCount={ORDERS_DATE_PICKER_CALENDER_COUNT}
                      maxSelectionEnd={ORDERS_DATE_PICKER_MAX_SELECTION_DAYS}
                    />
                    <MiButton
                      title="Today"
                      variant={BUTTON_VARIANTS.CONTAINED}
                      color={BUTTON_COLORS.PRIMARY}
                      startIcon={{
                        path: mdiCalendarRefresh,
                      }}
                      onClick={handleResetDateRange}
                      disabled={isDateRangeDisabled()}
                      disableRipple
                      data-testid="reset-button"
                    />
                  </Box>
                )}
                <OrdersList
                  activeTab={activeTab}
                  orderItems={orderItems}
                  orderCardConfig={orderCardConfig}
                  orderHoverCardConfig={orderHoverCardConfig}
                  tenantHolidays={tenantHolidays}
                  refresh={refresh}
                  isLoading={isLoading}
                  setOrderItems={setOrderItems}
                  getOrderItems={getOrderItems}
                  search={debouncedSearch}
                  refreshPagination={activeTab}
                  orderReasons={orderReasons}
                  onRefresh={handleRefresh}
                />
              </Box>
            </Box>
          </Box>
        )}
      <OrderActivityDrawer
        open={isOrderActivityDrawerOpen}
        onClose={closeDrawer}
        order={selectedOrder}
        getOrderActivity={getOrderActivity}
        onRefresh={(order) => {
          setSelectedOrder(order);
          handleRefresh();
        }}
      />
      {emailDrawer && (
        <OrdersEmailDrawer
          open={emailDrawer.isOpen}
          entity={emailDrawer.order}
          entityType={EMAIL_ENTITY_TYPE.ORDER}
          templateType={emailDrawer.templateType}
          onClose={handleEmailDrawerClose}
        />
      )}
      {emailPreview && (
        <EmailActivityPreviewModal
          isEmailModalOpen
          activity={emailPreview}
          details={emailPreview}
          closeEmailModal={handleEmailPreviewModalClose}
        />
      )}
      <OrdersPrintModal
        open={isPrintModalOpen}
        isLoading={isLoading}
        selectedOrderTemplate={filters.selectedOrderTemplate}
        onPrint={handlePrintOrders}
        onClose={handlePrintModalClose}
      />
    </>
  );
}

export const Orders = memo(() => {
  return (
    <OrdersProvider>
      <OrdersContent />
    </OrdersProvider>
  );
});

const useStyles = makeStyles()((theme) => ({
  listContainer: {
    position: "relative",
    borderRadius: theme.spacing(1.5),
    backgroundColor: theme.palette.common.white,
    boxShadow: "0px 1px 2px 0px rgba(0,0,0,.2)",
    overflow: "hidden",
    padding: 8,
    display: "flex",
    flex: 1,
  },
  actionContainer: {
    display: "flex",
    flexDirection: "row",
    justifyContent: "flex-end",
    alignItems: "center",
  },
  exportButton: {
    marginLeft: theme.spacing(2),
    "&:hover": {
      backgroundColor: theme.palette.grey.light,
    },
  },
  searchField: {
    width: 250,
  },
}));
